import React, { useState, useEffect } from 'react';
import { twoFactorVerify } from '../services/api';
import { twoFactorPrompt } from '../utils/twoFactorPrompt';
import './ConfirmDeleteModal.css';
import './TwoFactor.css';

// Global step-up prompt (mounted once in App.js). A protected action the server
// answered with 403 TWO_FACTOR_REQUIRED waits here for a fresh code; on success
// the API client retries the original request, on cancel it rejects it.
export default function TwoFactorPromptModal() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => twoFactorPrompt.subscribe((isOpen) => {
    setOpen(isOpen);
    if (isOpen) { setCode(''); setError(''); setAttemptsLeft(null); setBusy(false); }
  }), []);

  if (!open) return null;

  const cancel = () => { if (!busy) twoFactorPrompt.reject(); };

  const submit = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\s+/g, '').toUpperCase();
    if (!clean) return;
    setBusy(true);
    setError('');
    try {
      await twoFactorVerify(clean);
      twoFactorPrompt.resolve();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || 'Could not verify the code. Please try again.');
      setAttemptsLeft(typeof data?.attempts_left === 'number' ? data.attempts_left : null);
      setCode('');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay tf-prompt-overlay" onClick={cancel}>
      <div className="modal-content" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Confirm it&apos;s you</h3>
          <button type="button" onClick={cancel} className="close-button" disabled={busy}>&times;</button>
        </div>
        <form className="modal-body" style={{ textAlign: 'left' }} onSubmit={submit}>
          <p style={{ margin: '0 0 var(--spacing-sm)' }}>This change needs a code from your authenticator app.</p>
          <input
            className="form-control tf-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={11}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            aria-label="Authenticator code"
          />
          {error && (
            <p className="tf-error" role="alert">
              {error}{attemptsLeft !== null ? ` (${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left)` : ''}
            </p>
          )}
          <div className="tf-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Verify'}</button>
            <button type="button" className="btn btn-outline" onClick={cancel} disabled={busy}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
